import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { api, ApiError, setToken } from '../../core/api-client';
import { GoogleSignInButton } from '../../core/auth/GoogleSignInButton';
import { TurnstileWidget } from '../components/TurnstileWidget';
import { MarketingPageHead } from '../MarketingPageHead';

interface SignupResponse {
  tenant: { id: string; slug: string };
  ownerEmail: string;
  magicLinkSent: boolean;
}

function slugify(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 40);
}

export function SignupPage() {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = React.useState('');
  const [ownerName, setOwnerName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [autoSlug, setAutoSlug] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<SignupResponse | null>(null);
  const [turnstileToken, setTurnstileToken] = React.useState<string>('');
  const widgetEnabled = !!process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const [googleCredential, setGoogleCredential] = React.useState<string | null>(null);

  React.useEffect(() => {
    setAutoSlug(slugify(companyName));
  }, [companyName]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (googleCredential) {
        const r = await api<{ token: string }>('/api/public/signup', {
          method: 'POST',
          noRedirectOn401: true,
          body: {
            companyName: companyName.trim(),
            slug: autoSlug,
            googleCredential,
          },
        });
        // Hard navigation: AuthProvider re-mounts and picks the token up.
        setToken(r.token);
        window.location.replace('/dashboard');
        return;
      }
      const r = await api<SignupResponse>('/api/public/signup', {
        method: 'POST',
        noRedirectOn401: true,
        body: {
          companyName: companyName.trim(),
          ownerName: ownerName.trim(),
          ownerEmail: email.trim(),
          slug: autoSlug,
          turnstileToken,
        },
      });
      setSuccess(r);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        if (err.code === 'invalid_google_credential' || err.code === 'oauth_not_configured') {
          setGoogleCredential(null);
        }
      }
      else setError(String((err as any)?.message ?? err));
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <section className="min-h-[80vh] flex items-center px-6 py-20">
        <div className="max-w-md mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="rounded-3xl bg-white/75 dark:bg-white/[0.04] backdrop-blur-xl ring-1 ring-ink/[0.08] dark:ring-white/10 shadow-card dark:shadow-none p-8 text-center"
          >
            <div className="mx-auto h-14 w-14 rounded-full bg-lime-neon flex items-center justify-center mb-5 shadow-pill">
              <Check className="h-6 w-6 text-ink" strokeWidth={3} />
            </div>
            <h1 className="font-light text-display-md text-ink dark:text-white leading-tight tracking-[-0.025em]">
              Check your inbox.
            </h1>
            <p className="mt-4 text-[14.5px] text-ink/65 dark:text-white/65 leading-relaxed">
              We sent a magic link to <span className="text-ink dark:text-white font-bold">{success.ownerEmail}</span>.
              Click it to finish setting up your workspace and your free trial.
            </p>
            <p className="mt-5 text-[12.5px] text-ink/55 dark:text-white/55">
              Your workspace: <code className="font-mono text-ink dark:text-white">{success.tenant.slug}</code>
            </p>
            <Link to="/login" className="mt-7 inline-block text-[13.5px] font-bold text-coral underline-offset-4 hover:underline">
              Or sign in if you already have access
            </Link>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-[80vh] py-16 lg:py-24 px-6">
      <MarketingPageHead
        title="Start free"
        description="Create your Worqly workspace in 60 seconds. Seven days free, no card required."
      />
      <div className="max-w-5xl mx-auto grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-coral mb-3">Start free</p>
          <h1 className="font-bold text-hero-lg text-ink dark:text-white leading-[1.02] tracking-[-0.03em] text-balance">
            Spin up your <span className="italic text-coral">workspace</span> in under a minute.
          </h1>
          <p className="mt-6 text-body-lg leading-relaxed text-ink/65 dark:text-white/65 text-pretty">
            Every module enabled. No card required. We'll email you a magic link to set
            your password and walk in.
          </p>
          <ul className="mt-7 space-y-3 text-[14px] text-ink/85 dark:text-white/80">
            {['No credit card', 'Cancel anytime', 'Your data, your tenant', 'Multi-currency aware'].map((b) => (
              <li key={b} className="flex items-start gap-2.5">
                <Check className="h-4 w-4 text-coral mt-[2px]" strokeWidth={2.5} />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-7">
          <motion.form
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            onSubmit={onSubmit}
            className="rounded-3xl bg-white/75 dark:bg-white/[0.04] backdrop-blur-xl ring-1 ring-ink/[0.08] dark:ring-white/10 shadow-card dark:shadow-none p-7 lg:p-9 space-y-5"
          >
            <div className="space-y-2">
              <Label htmlFor="companyName">Company name</Label>
              <Input
                id="companyName"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Acme Co."
                required
                autoFocus
                autoComplete="organization"
              />
              {autoSlug ? (
                <p className="text-[12px] text-muted-foreground/80">
                  Workspace URL: <code className="font-mono text-foreground/85">worqly.io/w/{autoSlug}</code>
                </p>
              ) : null}
            </div>

            {googleCredential ? (
              <p className="text-[13px] text-ink/70 dark:text-white/70 bg-lime-neon/15 ring-1 ring-lime-neon/30 rounded-2xl px-3 py-2">
                ✓ Google account connected — just name your workspace and you're in.{' '}
                <button type="button" className="font-bold underline-offset-2 hover:underline" onClick={() => setGoogleCredential(null)}>
                  Use email instead
                </button>
              </p>
            ) : (
              <>
                <GoogleSignInButton text="signup_with" onCredential={(c) => { setError(null); setGoogleCredential(c); }} />
                <div className="flex items-center gap-3" aria-hidden>
                  <div className="h-px flex-1 bg-ink/10 dark:bg-white/15" />
                  <span className="text-[11px] uppercase tracking-[0.14em] font-bold text-ink/40 dark:text-white/40">or sign up with email</span>
                  <div className="h-px flex-1 bg-ink/10 dark:bg-white/15" />
                </div>
              </>
            )}

            {!googleCredential && (
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="ownerName">Your name</Label>
                  <Input
                    id="ownerName"
                    value={ownerName}
                    onChange={(e) => setOwnerName(e.target.value)}
                    placeholder="Jane Doe"
                    required
                    autoComplete="name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Work email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="jane@acme.co"
                    required
                    autoComplete="email"
                  />
                </div>
              </div>
            )}

            {error ? (
              <p className="text-[13px] text-destructive">{error}</p>
            ) : null}

            {!googleCredential && (
              <TurnstileWidget onToken={setTurnstileToken} onExpire={() => setTurnstileToken('')} />
            )}

            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={
                submitting ||
                !companyName.trim() ||
                (!googleCredential && (!ownerName.trim() || !email.trim() || (widgetEnabled && !turnstileToken)))
              }
            >
              {submitting
                ? 'Creating workspace…'
                : googleCredential
                  ? <>Create workspace &amp; go to dashboard<ArrowUpRight className="ml-1 h-4 w-4" /></>
                  : <>Create workspace &amp; send magic link<ArrowUpRight className="ml-1 h-4 w-4" /></>}
            </Button>

            <p className="text-[12px] text-ink/55 dark:text-white/55 leading-relaxed">
              By signing up you agree to our terms. We'll never email you anything you didn't ask for.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
